type MediaElement = HTMLAudioElement | HTMLVideoElement;

class Player {
  private media: MediaElement | null;
  private _src: string;
  private boundTimeUpdate: (() => void) | null;
  private boundPause: (() => void) | null;
  private boundLoadedMetadata: (() => void) | null;

  constructor() {
    this.media = null;
    this._src = '';
    this.boundTimeUpdate = null;
    this.boundPause = null;
    this.boundLoadedMetadata = null;
  }

  // 设置媒体元素
  setMediaElement(element: MediaElement) {
    this.removeAllEventListeners();
    this.media = element;
    if (this._src) {
      this.media.src = this._src;
    }
    this.initEvent();
  }

  set src(value: string) {
    if (this._src !== value) {
      this._src = value;
      if (this.media) {
        this.media.src = value;
      }
    }
  }

  get src(): string {
    return this._src;
  }

  get duration(): number {
    return this.media?.duration || 0;
  }

  get currentTime(): number {
    return this.media?.currentTime || 0;
  }

  set currentTime(value: number) {
    if (this.media) {
      this.media.currentTime = value;
    }
  }

  // 事件回调
  onLoadedMetadata() {}
  onTimeUpdate(time: number) {}
  onPause() {}
  onPlay() {}
  onEnded() {}
  onError(error: MediaError | null) {}

  async play() {
    if (this.media) {
      try {
        await this.media.play();
      } catch (error) {
        this.onError(this.media.error);
      }
    }
  }

  pause() {
    this.media?.pause();
  }

  private initEvent() {
    if (!this.media) return;

    this.boundTimeUpdate = () => {
      if (this.media) {
        this.onTimeUpdate(this.media.currentTime * 1000);
      }
    };

    this.boundPause = () => {
      this.onPause();
    };

    this.boundLoadedMetadata = () => {
      this.onLoadedMetadata();
    };

    this.media.addEventListener('timeupdate', this.boundTimeUpdate);
    this.media.addEventListener('pause', this.boundPause);
    this.media.addEventListener('loadedmetadata', this.boundLoadedMetadata);
    this.media.addEventListener('play', () => this.onPlay());
    this.media.addEventListener('ended', () => this.onEnded());
    this.media.addEventListener('error', () => this.onError(this.media?.error || null));
  }

  private removeAllEventListeners() {
    if (this.media) {
      if (this.boundTimeUpdate) {
        this.media.removeEventListener('timeupdate', this.boundTimeUpdate);
      }
      if (this.boundPause) {
        this.media.removeEventListener('pause', this.boundPause);
      }
      if (this.boundLoadedMetadata) {
        this.media.removeEventListener('loadedmetadata', this.boundLoadedMetadata);
      }
      this.media.removeEventListener('play', () => this.onPlay());
      this.media.removeEventListener('ended', () => this.onEnded());
      this.media.removeEventListener('error', () =>
        this.onError(this.media?.error || null),
      );
    }
  }

  destroy() {
    if (this.media) {
      this.pause();
      this.removeAllEventListeners();
      this.media = null;
      this.boundTimeUpdate = null;
      this.boundPause = null;
      this.boundLoadedMetadata = null;
      this._src = '';
    }
  }
}

// 导出单个实例即可
export const player = new Player();
